import { computed } from 'vue';
import { CoreTypes, typeOwner, type ObjectRecord } from '@vp/shared-types';
import { commands } from '@vp/core';
import { live, ui } from './bridge';
import { moduleDockDefault, moduleStyleCaps } from '../modules';
import { settings } from '../platform';

/** Reactive helpers over the current selection. */
export const selectedObjects = computed<ObjectRecord[]>(() => {
  void ui.docRev;
  const ed = live.editor;
  if (!ed) return [];
  return ui.selection.map((id) => ed.doc.get(id)).filter((o): o is ObjectRecord => !!o);
});

/** Leaf objects (group members expanded) — targets for style changes. */
export const styleTargets = computed<ObjectRecord[]>(() => {
  void ui.docRev;
  const ed = live.editor;
  if (!ed) return [];
  return commands
    .withDescendants(ed.doc, ui.selection)
    .map((id) => ed.doc.get(id))
    .filter((o): o is ObjectRecord => !!o && o.type !== CoreTypes.group);
});

export const primary = computed<ObjectRecord | null>(() => styleTargets.value[0] ?? null);

export const allLocked = computed(() => selectedObjects.value.length > 0 && selectedObjects.value.every((o) => o.locked));
export const hasGroup = computed(() => selectedObjects.value.some((o) => o.type === CoreTypes.group));

/** The module that owns every object of the selection, or null (core objects, several modules, mixed). */
function soleOwner(objs: ObjectRecord[]): string | null {
  const owner = objs.length ? typeOwner(objs[0].type) : null;
  return owner && objs.every((o) => typeOwner(o.type) === owner) ? owner : null;
}

/**
 * Do the properties of this selection live in the right sidebar? For objects of one module: the
 * user's choice for that module, else the module's default (manifest `propertiesIn`); otherwise
 * the general setting.
 */
export function propsDockedFor(objs: ObjectRecord[]): boolean {
  const owner = soleOwner(objs);
  if (owner) {
    const chosen = settings.propsDockedModules[owner];
    if (chosen !== undefined) return chosen;
    const def = moduleDockDefault(objs[0].type);
    if (def !== undefined) return def;
  }
  return settings.propsDocked;
}

/** The user moves the properties of this selection to the sidebar or back (remembered per module for module objects). */
export function setPropsDockedFor(objs: ObjectRecord[], docked: boolean): void {
  const owner = soleOwner(objs);
  if (owner) settings.propsDockedModules = { ...settings.propsDockedModules, [owner]: docked };
  else settings.propsDocked = docked;
}

export function isShapeLike(o: ObjectRecord): boolean {
  return o.type !== CoreTypes.line && o.type !== CoreTypes.connector && o.type !== CoreTypes.image && o.type !== CoreTypes.group;
}

export function supportsFill(o: ObjectRecord): boolean {
  // strokes and open paths have no inside to fill
  if (o.type === CoreTypes.pen || o.type === CoreTypes.diagram || (o.type === CoreTypes.path && o.props['closed'] !== true)) return false;
  if (!o.type.startsWith('core:')) return moduleStyleCaps(o.type).style;
  return isShapeLike(o);
}

export function supportsText(o: ObjectRecord): boolean {
  const types: string[] = [CoreTypes.rect, CoreTypes.ellipse, CoreTypes.triangle, CoreTypes.diamond, CoreTypes.text, CoreTypes.sticker, CoreTypes.frame, CoreTypes.connector, CoreTypes.line];
  return types.includes(o.type) || (!o.type.startsWith('core:') && moduleStyleCaps(o.type).text);
}

export function supportsStroke(o: ObjectRecord): boolean {
  if (!o.type.startsWith('core:')) return moduleStyleCaps(o.type).style;
  return o.type !== CoreTypes.group && o.type !== CoreTypes.diagram;
}
